import * as swcHelpers from '@swc/helpers';
import { Locales } from '@grandvisionhq/state';
import { getAttrValue, isDefined, getLocalizedAttrValue, getJoinedLocalizedAttrValues, getLocalizedValue } from '@grandvisionhq/utils';
import { calcPrice, priceVatCalc } from '../StructuredData/actions/helpers.js';
import { LineItemType, PurchaseType, Category } from './types.js';

const getNameFromVariant = (variant)=>{
    const brand = getAttrValue(variant, 'brand');
    const brandModel = getAttrValue(variant, 'brandModel');
    const brandModelCode = getAttrValue(variant, 'brandModelCode');
    const name = [
        ...new Set([
            brand,
            brandModel,
            brandModelCode
        ].filter(Boolean))
    ].join(' ').trim();
    return name;
};
const getPrices = (lineItem)=>{
    var ref, ref1;
    const variant = 'variant' in lineItem ? lineItem.variant : lineItem.bundleMainLineItem.variant;
    const vat = 'variant' in lineItem ? (ref = lineItem.taxRate) === null || ref === void 0 ? void 0 : ref.amount : lineItem.bundleMainLineItem.taxRate.amount;
    let pricing;
    if (!variant) {
        console.warn('No variant data found.');
        return null;
    }
    if (typeof vat === 'undefined') {
        console.warn(`No VAT amount found for variant with SKU ${variant.sku}.`);
        return null;
    }
    if ('variant' in lineItem) {
        var _prices;
        pricing = ((_prices = variant.prices) !== null && _prices !== void 0 ? _prices : []).find((price)=>typeof price.value.centAmount !== 'undefined'
        );
    } else {
        pricing = lineItem.price;
    }
    if (!pricing) {
        console.warn(`Variant with SKU ${variant.sku} doesn't have any pricing.`);
        return null;
    }
    const regularPrice = pricing.value.centAmount;
    var _centAmount;
    const discountedPrice = (_centAmount = (ref1 = pricing.discounted) === null || ref1 === void 0 ? void 0 : ref1.value.centAmount) !== null && _centAmount !== void 0 ? _centAmount : null;
    const discountAmount = discountedPrice !== null ? regularPrice - discountedPrice : 0;
    const { vatAmount  } = discountedPrice !== null ? priceVatCalc(discountedPrice, vat) : priceVatCalc(regularPrice, vat);
    return {
        original: calcPrice(regularPrice),
        discountedPrice: discountedPrice !== null ? calcPrice(discountedPrice) : calcPrice(regularPrice),
        discountAmount: calcPrice(discountAmount),
        vatAmount: calcPrice(vatAmount)
    };
};
const getSharedAttributes = (lineItem, lineItemType, purchaseType)=>{
    const prices = getPrices(lineItem);
    const isBundleLineItem = 'variant' in lineItem;
    const sku = isBundleLineItem ? lineItem.variant.sku : lineItem.bundleMainLineItem.variant.sku;
    const name = isBundleLineItem ? getNameFromVariant(lineItem.variant) : '';
    return {
        name,
        id: sku || '',
        price: (prices === null || prices === void 0 ? void 0 : prices.discountedPrice) || '',
        dimension143: purchaseType ? purchaseType : PurchaseType.OneTime,
        dimension144: lineItemType,
        dimension145: 'true',
        metric140: (prices === null || prices === void 0 ? void 0 : prices.original) || '',
        metric141: (prices === null || prices === void 0 ? void 0 : prices.discountAmount) || '',
        metric142: (prices === null || prices === void 0 ? void 0 : prices.vatAmount) || '',
        quantity: lineItem.quantity
    };
};
const getSunglassesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.Sunglasses,
        brand,
        variant: getLocalizedAttrValue(variant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getLocalizedAttrValue(variant, 'frameColor', locale).toLowerCase(),
        dimension140: variant.sku || ''
    });
};
const getReadyReadersAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getNameFromVariant(lineItem.variant),
        category: Category.ReadyReaders,
        brand,
        variant: getLocalizedAttrValue(variant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getLocalizedAttrValue(variant, 'frameColor', locale).toLowerCase()
    });
};
const getAccessoriesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getNameFromVariant(lineItem.variant),
        category: Category.Accessories,
        brand,
        variant: getLocalizedAttrValue(variant, 'accessoryType', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'material', locale).toLowerCase(),
        dimension147: getJoinedLocalizedAttrValues(variant, 'accessoryColor', locale).toLowerCase(),
        dimension140: variant.sku || ''
    });
};
const getContactLensesAttributes = (locale, lineItem)=>{
    const variant = lineItem.variant;
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getLocalizedValue(lineItem.name, locale),
        brand: getAttrValue(variant, 'brand') || '',
        category: Category.ContactLenses,
        variant: getLocalizedAttrValue(variant, 'lensType', locale),
        dimension148: getLocalizedAttrValue(variant, 'replacementFrequency', locale),
        dimension146: getJoinedLocalizedAttrValues(variant, 'material', locale),
        dimension140: variant.sku || ''
    });
};
const getPrescriptionGlassesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Bundle);
    const category = lineItem.bundleMainLineItem.productType === 'sunglasses' ? Category.Sunglasses : Category.Optical;
    const { variant , brand , brandModel , brandModelCode  } = lineItem.bundleMainLineItem;
    const name = [
        ...new Set([
            brand,
            brandModel,
            brandModelCode
        ].filter(Boolean))
    ].join(' ');
    const frameMaterial = getJoinedLocalizedAttrValues(variant, 'frameMaterial', locale).toLowerCase();
    const frameColor = getJoinedLocalizedAttrValues(variant, 'frameColor', locale).toLowerCase();
    const frameShape = getJoinedLocalizedAttrValues(variant, 'frameShape', locale).toLowerCase();
    const gender = getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase();
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name,
        brand: lineItem.bundleMainLineItem.brand || '',
        category,
        variant: frameShape,
        dimension140: variant.sku || ''
    }, frameMaterial ? {
        dimension146: frameMaterial
    } : null, frameColor ? {
        dimension147: frameColor
    } : null, gender ? {
        dimension16: gender
    } : null);
};
const getSolutionsAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const brand = getAttrValue(lineItem.variant, 'brand') ? String(getAttrValue(lineItem.variant, 'brand')) : '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getLocalizedValue(lineItem.name, locale),
        category: Category.Solutions,
        brand,
        variant: 'solution'
    });
};
const getContactLensesBundleBeneluxAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Bundle, PurchaseType.CLBBSubscription);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getLocalizedValue(lineItem.name, locale),
        category: Category.ContactLenses,
        variant: getLocalizedAttrValue(lineItem.variant, 'lensType', locale)
    });
};
/**
 *  Mappers for GQL Order line item data to product data for gvPurchaseEvent
 */ const transformValue = (data, locale)=>data.filter((item)=>item[locale] || item[locale.substring(0, 2)]
    ).reduce((_, next)=>next[locale] || next[locale.substring(0, 2)]
    , '')
;
const formatAttribute = (attributes)=>({
        [attributes.locale]: attributes.value
    })
;
const formatAttributes = (attributes)=>({
        [attributes.locale]: attributes.values.join(',')
    })
;
const transformToEventPayload = ({ dimension147 , dimension16 , dimension146 , variant , locale  })=>{
    return {
        variant: variant ? transformValue(variant, locale) : '',
        dimension16: dimension16 ? transformValue(dimension16, locale) : '',
        dimension146: dimension146 ? transformValue(dimension146, locale) : '',
        dimension147: dimension147 ? transformValue(dimension147, locale) : ''
    };
};
const getOrderLineItemSharedAttributes = (locale, lineItem, lineItemType, purchaseType)=>{
    var ref;
    const localizedName = lineItem.product.name.map(formatAttribute);
    var ref2;
    return {
        name: localizedName ? transformValue(localizedName, locale) : '',
        id: lineItem.product.sku || '',
        price: `${(ref2 = calcPrice(lineItem === null || lineItem === void 0 ? void 0 : lineItem.price.total.gross)) !== null && ref2 !== void 0 ? ref2 : ''}`,
        dimension143: purchaseType ? purchaseType : PurchaseType.OneTime,
        dimension144: lineItemType,
        dimension145: 'true',
        metric140: `${calcPrice(lineItem === null || lineItem === void 0 ? void 0 : lineItem.price.total.subtotal) || ''}` || '',
        metric141: `${calcPrice(lineItem === null || lineItem === void 0 ? void 0 : lineItem.price.total.discount.amount) || ''}`,
        metric142: `${calcPrice(lineItem === null || lineItem === void 0 ? void 0 : (ref = lineItem.price) === null || ref === void 0 ? void 0 : ref.total.tax.amount) || ''}`,
        quantity: lineItem.quantity
    };
};
const getOrderLineItemSunglassesAttributes = (locale, lineItem)=>{
    var ref, ref3, ref4, ref5;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Single);
    const productMetaData = lineItem.product.additionalProductDetails;
    const frameShape = productMetaData === null || productMetaData === void 0 ? void 0 : (ref = productMetaData.frameShape) === null || ref === void 0 ? void 0 : ref.map(formatAttribute);
    const gender = productMetaData === null || productMetaData === void 0 ? void 0 : (ref3 = productMetaData.gender) === null || ref3 === void 0 ? void 0 : ref3.map(formatAttributes);
    const frameMaterial = productMetaData === null || productMetaData === void 0 ? void 0 : (ref4 = productMetaData.frameMaterial) === null || ref4 === void 0 ? void 0 : ref4.map(formatAttribute);
    const frameColor = productMetaData === null || productMetaData === void 0 ? void 0 : (ref5 = productMetaData.frameColor) === null || ref5 === void 0 ? void 0 : ref5.map(formatAttributes);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.Sunglasses,
        brand: lineItem.product.brand || ''
    }, transformToEventPayload({
        dimension147: frameColor,
        dimension16: gender,
        dimension146: frameMaterial,
        variant: frameShape,
        locale
    }), {
        dimension140: lineItem.product.sku || ''
    });
};
const getOrderPrescriptionGlassesAttributes = (locale, lineItem)=>{
    var ref, ref6;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Bundle);
    const category = Category.Optical;
    const name = (ref = lineItem.product) === null || ref === void 0 ? void 0 : (ref6 = ref.name) === null || ref6 === void 0 ? void 0 : ref6.map(formatAttribute);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: name ? transformValue(name, locale) : '',
        brand: lineItem.product.brand || '',
        category,
        variant: '',
        dimension16: '',
        dimension146: '',
        dimension147: '',
        dimension140: lineItem.product.sku || ''
    });
};
const getOrderLineItemReadyReadersAttributes = (locale, lineItem)=>{
    var ref, ref7, ref8, ref9;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Single);
    const productMetaData = lineItem.product.additionalProductDetails;
    const frameShape = productMetaData === null || productMetaData === void 0 ? void 0 : (ref = productMetaData.frameShape) === null || ref === void 0 ? void 0 : ref.map(formatAttribute);
    const gender = productMetaData === null || productMetaData === void 0 ? void 0 : (ref7 = productMetaData.gender) === null || ref7 === void 0 ? void 0 : ref7.map(formatAttributes);
    const frameMaterial = productMetaData === null || productMetaData === void 0 ? void 0 : (ref8 = productMetaData.frameMaterial) === null || ref8 === void 0 ? void 0 : ref8.map(formatAttribute);
    const frameColor = productMetaData === null || productMetaData === void 0 ? void 0 : (ref9 = productMetaData.frameColor) === null || ref9 === void 0 ? void 0 : ref9.map(formatAttributes);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        brand: lineItem.product.brand || '',
        category: Category.ReadyReaders
    }, transformToEventPayload({
        dimension147: frameColor,
        dimension16: gender,
        dimension146: frameMaterial,
        variant: frameShape,
        locale
    }), {
        dimension140: lineItem.product.sku || ''
    });
};
const getOrderLineItemAccessoriesAttributes = (locale, lineItem)=>{
    var ref, ref10, ref11, ref12;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Single);
    const productMetaData = lineItem.product.additionalProductDetails;
    const accessoryType = productMetaData === null || productMetaData === void 0 ? void 0 : (ref = productMetaData.accessoryType) === null || ref === void 0 ? void 0 : ref.map(formatAttribute);
    const gender = productMetaData === null || productMetaData === void 0 ? void 0 : (ref10 = productMetaData.gender) === null || ref10 === void 0 ? void 0 : ref10.map(formatAttributes);
    const material = productMetaData === null || productMetaData === void 0 ? void 0 : (ref11 = productMetaData.material) === null || ref11 === void 0 ? void 0 : ref11.map(formatAttribute);
    const accessoryColor = productMetaData === null || productMetaData === void 0 ? void 0 : (ref12 = productMetaData.accessoryColor) === null || ref12 === void 0 ? void 0 : ref12.map(formatAttributes);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.Accessories,
        brand: lineItem.product.brand || ''
    }, transformToEventPayload({
        variant: accessoryType,
        dimension146: material,
        dimension16: gender,
        dimension147: accessoryColor,
        locale
    }), {
        dimension140: lineItem.product.sku || ''
    });
};
const getOrderLineItemSolutionsAttributes = (locale, lineItem)=>{
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Single);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.Solutions,
        brand: lineItem.product.brand || '',
        variant: 'solution'
    });
};
const getOrderLineItemContactLensesAttributes = (locale, lineItem)=>{
    var ref, ref13, ref14, ref15, ref16;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Single);
    const lenseType = (ref = lineItem.product) === null || ref === void 0 ? void 0 : (ref13 = ref.lensType) === null || ref13 === void 0 ? void 0 : ref13.map(formatAttribute);
    const material = (ref14 = lineItem.product) === null || ref14 === void 0 ? void 0 : (ref15 = ref14.material) === null || ref15 === void 0 ? void 0 : ref15.map(formatAttribute);
    const replacementFrequency = (ref16 = lineItem.product) === null || ref16 === void 0 ? void 0 : ref16.replacementFrequency.map(formatAttribute);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        brand: lineItem.product.brand || '',
        category: Category.ContactLenses,
        variant: lenseType ? transformValue(lenseType, locale) : '',
        dimension148: replacementFrequency ? transformValue(replacementFrequency, locale) : '',
        dimension146: material ? transformValue(material, locale) : '',
        dimension140: lineItem.product.sku || ''
    });
};
const getOrderLineItemContactLensesBundleBeneluxAttributes = (locale, lineItem)=>{
    var ref, ref17;
    const sharedAttributes = getOrderLineItemSharedAttributes(locale, lineItem, LineItemType.Bundle, PurchaseType.CLBBSubscription);
    const lenseType = (ref = lineItem.product) === null || ref === void 0 ? void 0 : (ref17 = ref.lensType) === null || ref17 === void 0 ? void 0 : ref17.map(formatAttribute);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.ContactLenses,
        variant: lenseType ? transformValue(lenseType, locale) : ''
    });
};
// Data in the Data Layer should always be English, therefore hardcoded locale
const bindLocaleToMapper = (mapper)=>mapper.bind(null, Locales['en-GB'])
;
// Mapping for legacy line item product type
const lineItemDataLayerMapperTable = {
    Sunglasses: bindLocaleToMapper(getSunglassesAttributes),
    'Contact-lenses': bindLocaleToMapper(getContactLensesAttributes),
    Solutions: bindLocaleToMapper(getSolutionsAttributes),
    'Contact-lens-bundles': bindLocaleToMapper(getContactLensesBundleBeneluxAttributes),
    'Ready-readers': bindLocaleToMapper(getReadyReadersAttributes),
    Accessories: bindLocaleToMapper(getAccessoriesAttributes)
};
const orderLineItemDataLayerMapperTable = {
    SUNGLASSES: bindLocaleToMapper(getOrderLineItemSunglassesAttributes),
    CONTACT_LENSES: bindLocaleToMapper(getOrderLineItemContactLensesAttributes),
    SOLUTIONS: bindLocaleToMapper(getOrderLineItemSolutionsAttributes),
    CONTACT_LENSES_BUNDLES: bindLocaleToMapper(getOrderLineItemContactLensesBundleBeneluxAttributes),
    READY_READERS: bindLocaleToMapper(getOrderLineItemReadyReadersAttributes),
    ACCESSORIES: bindLocaleToMapper(getOrderLineItemAccessoriesAttributes),
    FRAMES: bindLocaleToMapper(getOrderPrescriptionGlassesAttributes),
    // mapping to keep product type backwards compatible till BE changes to
    // normalized productType data is released product.productType
    sunglasses: bindLocaleToMapper(getOrderLineItemSunglassesAttributes),
    'Contact-lenses': bindLocaleToMapper(getOrderLineItemContactLensesAttributes),
    frames: bindLocaleToMapper(getOrderPrescriptionGlassesAttributes)
};
const lineItemToProductItem = (lineItem)=>{
    var ref;
    const productType = (ref = lineItem.productType.obj) === null || ref === void 0 ? void 0 : ref.name;
    if (!productType) {
        return;
    }
    const mapper = lineItemDataLayerMapperTable[productType];
    if (!mapper) {
        return;
    }
    return mapper(lineItem);
};
const orderLineItemToProductItem = (lineItem)=>{
    var ref;
    const productType = (ref = lineItem.product.currentProduct) === null || ref === void 0 ? void 0 : ref.type;
    if (!productType) {
        return;
    }
    const mapper = orderLineItemDataLayerMapperTable[productType];
    if (!mapper) {
        return;
    }
    return mapper(lineItem);
};
const mapLineItemToDataLayerProductObjects = (lineItems, prescriptionGlassesLineItems)=>{
    const productObjects = [];
    if (lineItems) {
        productObjects.push(...lineItems.map(lineItemToProductItem).filter(isDefined));
    }
    if (prescriptionGlassesLineItems) {
        productObjects.push(...prescriptionGlassesLineItems.map(getPrescriptionGlassesAttributes.bind(null, Locales['en-GB'])).filter(isDefined));
    }
    return productObjects;
};
const mapOrderLineItemToDataLayerProductObjects = (lineItems)=>{
    const productObjects = [];
    if (lineItems) {
        productObjects.push(...lineItems.map(orderLineItemToProductItem).filter(isDefined));
    }
    return productObjects;
};

export { getNameFromVariant, mapLineItemToDataLayerProductObjects, mapOrderLineItemToDataLayerProductObjects };
//# sourceMappingURL=utils.js.map
